<template>
  <section
    :class="[
      'relative',
      !section.full_width ? (isNarrow ? narrowClass : containerClass) : null,
      section.theme || $page.props.website.theme,
      section.theme_variant,
      section.divide ? 'divide' : null,
      section.divide,
      hasBackgroundImage ? 'overflow-hidden' : null,
      hasBackgroundParallax ? 'nt-parallax-host' : null
    ]"
    :style="{ zIndex: sectionZIndex }"
    :id="`section${section.id}`">
    <div
      v-if="hasBackgroundImage"
      :class="['pointer-events-none absolute z-0', hasBackgroundParallax ? 'nt-section-parallax -inset-x-8 -top-[15%] -bottom-[15%]' : '-inset-8']"
      :style="bgLayerStyle"></div>
    <div v-if="hasBackgroundDim" class="pointer-events-none absolute inset-0 z-0" :style="bgDimStyle"></div>
    <div
      :class="[
        '@container',
        hasBackgroundImage ? 'relative z-10' : null,
        centresContent ? (isNarrow ? `${narrowClass} px-6` : centredContainerClass) : null,
        paddingClass
      ]">
      <div :class="['grid', layoutClass, alignClass, gapClass]">
        <NtModule v-for="m in section.modules" :key="m.id" :page-module="m" />
      </div>
    </div>
  </section>
</template>

<script>
import { computed } from 'vue'
import NtModule from './NtModule.vue'
import { sectionBackgroundMixin } from '@/js/composables/sectionBackground.js'
import { sectionPaddingMixin } from '@/js/composables/sectionPadding.js'

export default {
  components: { NtModule },
  mixins: [sectionBackgroundMixin, sectionPaddingMixin],
  props: {
    section: {
      type: Object,
      required: true
    },
    index: {
      type: Number,
      required: true
    },
    maxIndex: {
      type: Number,
      required: true
    }
  },
  computed: {
    // Sections normally paint in a descending z-order (earlier sections above
    // later ones) so a section can overlap the one below it. Sections holding a
    // module that reaches UP past their own top edge are the exception — the
    // Boulevard valuation band's dome, the Palisades service highlight's
    // pulled-up text panel — and must out-stack the neighbour above them.
    // Guarded on the module flags, so every other section keeps the default order.
    sectionZIndex() {
      return this.hasModuleOverlappingAbove ? this.maxIndex + 1 : this.maxIndex - this.index
    },
    hasModuleOverlappingAbove() {
      return (this.section.modules || []).some((m) => m?.config?.curved_top || (m?.config?.overlap_above && m.config.overlap_above !== 'none'))
    },
    /**
     * A module that paints its own background across the block is given the
     * section's full width: it can't break out of the container itself (see
     * `Module::$rendersFullBleed`), and it insets its own content back to the
     * page container, so centring here would only box the background.
     *
     * Boxing one stays available and stays explicit — turning the section's
     * `full_width` off puts the container on the section itself, above this.
     */
    hasFullBleedModule() {
      return (this.section.modules || []).some((m) => m?.variant?.full_bleed)
    },
    // Wider than `hasFullBleedModule`: also true for a module that only wants
    // the full width because of its own settings (the property carousel's
    // full-width header band). That one still sits inside the section's
    // padding — only true full-bleed modules such as heroes drop it.
    hasFullWidthModule() {
      return this.hasFullBleedModule || (this.section.modules || []).some((m) => m?.full_bleed)
    },
    centresContent() {
      return this.section.full_width && this.section.content_centred && !this.hasFullWidthModule
    },
    gapClass() {
      // Spacing between modules. `gap_size` (none/small/medium/large) takes
      // precedence; when unset, fall back to the legacy `gaps` boolean so
      // existing sections render unchanged (true => medium, false => none).
      const size = this.section.gap_size || (this.section.gaps ? 'medium' : 'none')
      return { small: 'gap-8', medium: 'gap-16', large: 'gap-24' }[size] || null
    },
    layoutClass() {
      switch (this.section.layout_id) {
        case 1:
          return '@4xl:grid-cols-2'
        case 2:
          return '@5xl:grid-cols-[1fr_3fr]'
        case 3:
          return '@5xl:grid-cols-[3fr_1fr]'
        case 4:
          return '@5xl:grid-cols-[3fr_2fr]'
        case 5:
          return '@5xl:grid-cols-[2fr_3fr]'
        case 6:
          return '@5xl:grid-cols-3'
        default:
          return null
      }
    },
    alignClass() {
      switch (this.section.vertical_align_id) {
        case 1:
          return 'place-items-center'
        case 2:
          return 'place-items-end'
        default:
          return null
      }
    },
    isNarrow() {
      return this.section.layout_id === 7
    },
    isAtlas() {
      return this.$page.props.website?.base_template_uid === 'atlas'
    },
    // The single global content container. Atlas adds the shared px-6 lg:px-10
    // gutter so every section's content lines up with the header, hero and
    // search modules — all of which use `container mx-auto px-6 lg:px-10`. Other
    // templates keep the bare container (their modules pad themselves).
    containerClass() {
      return this.isAtlas ? 'container mx-auto px-6 lg:px-10' : 'container mx-auto'
    },
    // Same global gutter for full-width sections that opt to centre their content.
    centredContainerClass() {
      return this.isAtlas ? 'container mx-auto px-6 lg:px-10' : 'container mx-auto px-6'
    },
    narrowClass() {
      // Boulevard's and Plaza's narrow column matches the default width
      // (max-w-4xl), but needs an explicit width + horizontal padding: sections
      // are flex items (NtLayout is flex-col), so an mx-auto column with no
      // definite width shrinks to its content's min-content — collapsing the
      // text to one word per line. w-full fixes the shrink; px-6 keeps mobile
      // off the edges.
      return ['boulevard', 'plaza'].includes(this.$page.props.website?.base_template_uid) ? 'mx-auto w-full max-w-4xl px-6' : 'mx-auto max-w-4xl'
    }
  },
  provide() {
    return {
      section: computed(() => this.section)
    }
  }
}
</script>
